<template>
  <div
    class="jps-data-panel"
    v-if="d"
    :class="{ 'jdp-selectable': selectable }"
  >
    <BaseLink
      class="jdp-thumbnail"
      :nolink="nolink || isWebParts"
      :to="e.internalLink">
      <UiImageSelection
        :image="e.image"
        :type="type"
        :w="imageSize"
        :h="imageSize"
        image-usage="thumbnail"
      />
    </BaseLink>
    <div class="jdp-content">
      <h4 class="jps-h4 jps-data-title">
        <BaseLink
          :nolink="nolink || isWebParts || !e.internalLink"
          :to="e.internalLink">{{
          ls(e.title)
        }}</BaseLink>
        <BaseLink
          class="tablink is-size-7"
          v-if="tablink && e.internalLink"
          :to="e.internalLink"
          v-target
          :stop="true"
          >{{ lss('詳細を見る', 'See detail') }}</BaseLink
        >
      </h4>
      <slot>
        <div v-if="type === 'item'">
          <p class="jps-item-details" v-if="d.common">
            {{
              ljoinLists(
                d.common.contributor,
                d.common.temporal,
                d.common.location,
              )
            }}
            <br />
            <template v-if="isJps">
              {{ ls(dapLangGlobal.provider) }}:
              <UiOrgProviderSpan tag="span" :id="d.common.provider" />
            </template>
            <span v-if="isJps && d.common && d.common.provider !== d.common.ownerOrg">
              <BaseLink
                class="item-owner"
                :nolink="isWebParts || nolink"
                :to="'/organization/' + d.common.ownerOrg"
              >
                <BaseIcon name="mdi/link-variant" size="size-7" class="icon" />
                <UiOrgSpan :id="d.common.ownerOrg" class="text" />
              </BaseLink>
            </span>
            <BaseLink
              :nolink="isWebParts || nolink"
              :to="'/database/' + d.common.database"
              class="item-database"
            >
              <BaseIcon name="mdi/database" size="size-7" class="icon" />
              <UiDbSpan tag="span" :id="d.common.database" class="text" />
            </BaseLink>
          </p>
          <p class="jps-item-desc">{{ abb(lid(d), abbsize) }}</p>
        </div>
        <div v-else>
          <p class="jps-item-details">
            {{ abb(ls(e.description), abbsize) }}
          </p>
        </div>
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import vTarget from '~/directives/target'
import { dapLangGlobal } from '#imports'

const { isWebParts } = useWebParts()
const { ls, lid, lss, ljoinLists } = useI18n()
const { isJps } = useServiceType()
const { dataToListElement } = useListElement()
const dataStore = useDataStore()

interface Props {
  nolink?: boolean
  tablink?: boolean
  selectable?: boolean
  imageSize?: number
  abbsize?: number
  data?: any
  type?: Key
  id?: string
}
const props = withDefaults(defineProps<Props>(), {
  nolink: false,
  tablink: false,
  selectable: false,
  imageSize: 128,
  abbsize: 200,
  data: null,
  type: null,
  id: null,
})

const d = computed(() => {
  if (props.data) return props.data
  if (props.id) {
    let type: any = props.type === 'curation' ? 'curation_meta' : props.type
    return dataStore.get(type, props.id)
  }
  return null
})

const e = computed(() => {
  return dataToListElement(props.type, d.value)
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-data-panel {
  display: flex;
  border-radius: 0.25rem;
  border: 1px solid $jps-gray-300;
  background-color: white;
  padding: calc(0.25rem * 3);
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);

  .jdp-content {
    margin-left: 0.75rem;
    flex-grow: 1;
    .jps-data-title {
      display: flex;
      flex-direction: column;

      @include is-tablet {
        flex-direction: row;
      }

      .tablink {
        @include is-tablet {
          margin-left: auto;
          margin-right: 1rem;
        }
      }
    }
    .jps-item-details,
    .jps-item-desc {
      font-size: 0.75rem;
      line-height: calc(1.75 / 1.25);
      @include is-tablet {
        font-size: 0.875rem;
        line-height: calc(1.25 / 0.875);
      }
    }

    .item-owner,
    .item-database {
      padding-left: 0.5rem;

      .icon {
        vertical-align: middle;
        margin-right: 0.25rem;
      }

      .text {
        vertical-align: middle;
      }
    }
  }

  &.jdp-selectable {
    cursor: pointer;
    &:hover {
      border-width: 1px;
      border-color: $jps-gray-500;
      border-style: solid;
      transition: all 0.2s;
    }
  }
}
</style>
